/**
 * File: FilterButton.js
 * Origin: isotect
 * Description: FilterButton component used to toggle a filter modal.
 *
 * Version: v1.0.0
 * Created: 2026-07-14
 * Last Updated: 2026-07-16
 *
 * Used By: isotect
 * Maintainer: Zachary Dicks
 *
 * Notes:
 * - Changes in this file may affect all consuming frontend modules.
 * - See Git repository 'isotect' for full change history.
 */

// Third-party packages
import { useEffect, useState, useRef } from "react";

// Local project files
import { IconButton } from "../ui/Buttons";
import FilterModal from "./FilterModal";

/**
 * Renders a button that opens and closes a filter modal for configuring filters.
 *
 * @component
 *
 * @param {Object} props - Component props
 * @param {Object} props.schema - Configuration schema describing the buttons and available filters
 * @param {Object} props.filters - Currently applied filter values
 * @param {(filters: Object) => void} props.setFilters - Updates the applied filter values
 *
 * @returns {JSX.Element} A filter button and its associated filter modal
 */
function FilterButton({ schema, filters, setFilters }) {
    const ref = useRef(null);

    const [isOpen, setIsOpen] = useState(false);

    const filtersApplied = Object.keys(filters).length;

    useEffect(() => {
        if (!isOpen) return;

        document.addEventListener("pointerdown", handleOutsideClick);

        return () => {
            document.removeEventListener("pointerdown", handleOutsideClick);
        };
    }, [isOpen]);

    const handleClick = () => {
        setIsOpen((prev) => !prev);
    };

    const handleClose = () => {
        setIsOpen(false);
    };

    const handleOutsideClick = (event) => {
        if (ref.current && !ref.current.contains(event.target)) {
            handleClose();
        }
    };

    return (
        <>
            <div className="filter-button" ref={ref}>
                <IconButton {...schema.iconButton} onClick={handleClick} />
                {filtersApplied > 0 && (
                    <span className="filter-button__active-badge">
                        {filtersApplied}
                    </span>
                )}
                {isOpen && (
                    <div className="filter-button__modal">
                        <FilterModal
                            schema={schema.filterModal}
                            filters={filters}
                            setFilters={setFilters}
                            onClose={handleClose}
                        />
                    </div>
                )}
            </div>
        </>
    );
}

export default FilterButton;
